import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  IconButton,
  Tooltip,
} from '@civilgrid/ui/components';
import { useSnapshot } from 'valtio';
import { deleteAgencyLink as apiDeleteLink } from '~/api/civilsearch';
import type { AgencyLink } from '~/common/types';
import { Notify } from '~/components2/Notify';
import store from '../../../store';
import { linkTypeMap } from './common';

interface Props {
  link: AgencyLink;
}

export default function DeleteLink({ link }: Props) {
  const { agency } = useSnapshot(store);

  const onDelete = async () => {
    try {
      const res = await apiDeleteLink(agency!.id, link.id);
      if (!res.id) throw new Error('Bad response!');
      Notify.info({ message: 'Link successfully deleted' });
      store.fetchData(agency!.id);
    } catch (e) {
      console.error('Error deleting link: ', e);
      Notify.error({ message: 'Error deleting link' });
    }
  };

  return (
    <Dialog>
      <Tooltip text="Delete link" asChild>
        <DialogTrigger asChild>
          <IconButton
            variant="hollow"
            icon="trash"
            aria-label={`Delete link ${link.title || link.url}`}
            data-testid={`agency-link-delete-${link.id}`}
          />
        </DialogTrigger>
      </Tooltip>
      <DialogContent
        title="Delete Link?"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`agency-link-delete-dialog-${link.id}`}
      >
        <DialogBody className="min-w-[16rem] text-sm">
          <div className="flex flex-col space-y-4">
            <div>Confirm that you want to delete this link.</div>

            <ul className="list-inside">
              <li>
                <span className="font-bold">Title: </span>
                {link.title}
              </li>
              <li>
                <span className="font-bold">Description: </span>
                {link.description}
              </li>
              <li>
                <span className="font-bold">URL: </span>
                {link.url}
              </li>
              <li>
                <span className="font-bold">Type: </span>
                {linkTypeMap[link.type]}
              </li>
            </ul>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onDelete} data-testid={`agency-link-delete-confirm-${link.id}`}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
